import React from 'react';
import type { Layer, Flow, Node as DiagramNode, Edge, ThemePalette } from '../../types';
import { LAYER_PALETTE } from '../../constants';
import { usedViaPointer } from '../../onboarding';

interface LayerFlowPanelProps {
  isViewOnly: boolean;
  palette: ThemePalette;
  glassStyle: React.CSSProperties;
  
  // Tabs & UI state
  bottomPanelTab: 'layers' | 'flows';
  setBottomPanelTab: (tab: 'layers' | 'flows') => void;
  pendingLayerEdit: React.MutableRefObject<boolean>;
  editingLayerId: string | null;
  setEditingLayerId: (id: string | null) => void;
  editingFlowId: string | null;
  setEditingFlowId: (id: string | null) => void;
  
  // Layers
  layers: Layer[];
  activeLayerId: string;
  setActiveLayer: (id: string) => void;
  addLayer: (name: string, color: string) => void;
  updateLayer: (id: string, updates: Partial<Layer>) => void;
  removeLayer: (id: string) => void;
  
  // Flows
  flows: Flow[];
  activeFlowId: string | null;
  setActiveFlowId: (id: string | null) => void;
  addFlow: (name: string, nodeIds: string[], edgeIds: string[]) => void;
  renameFlow: (id: string, newName: string) => void;
  updateFlow: (id: string, nodeIds: string[], edgeIds: string[]) => void;
  removeFlow: (id: string) => void;
  
  // Diagram context
  selectedItems: string[];
  nodes: DiagramNode[];
  edges: Edge[];
}

export function LayerFlowPanel({
  isViewOnly, palette, glassStyle,
  bottomPanelTab, setBottomPanelTab, pendingLayerEdit, editingLayerId, setEditingLayerId, editingFlowId, setEditingFlowId,
  layers, activeLayerId, setActiveLayer, addLayer, updateLayer, removeLayer,
  flows, activeFlowId, setActiveFlowId, addFlow, renameFlow, updateFlow, removeFlow,
  selectedItems, nodes, edges
}: LayerFlowPanelProps) {
  return (
    <div style={glassStyle} className="backdrop-blur-md border rounded-2xl shadow-2xl flex-shrink-0 max-h-[260px] flex flex-col overflow-hidden w-[220px]" data-tour="layers">
      {/* Tab bar */}
      <div className="flex border-b flex-shrink-0" style={{ borderColor: palette.panelBorder }}>
        {(['layers', 'flows'] as const).map(tab => (
          <button
            key={tab}
            className="flex-1 py-2 text-[11px] font-semibold uppercase tracking-widest transition-colors"
            style={{
              color: bottomPanelTab === tab ? palette.panelText : palette.panelSubText,
              borderBottom: bottomPanelTab === tab ? `2px solid #818cf8` : '2px solid transparent',
            }}
            onClick={() => setBottomPanelTab(tab)}
          >{tab}</button>
        ))}
        {/* Add button per tab */}
        {bottomPanelTab === 'layers' && !isViewOnly && (
          <button
            title="Add layer"
            className="px-3 text-base leading-none font-bold transition-colors hover:opacity-100 opacity-60"
            style={{ color: palette.panelSubText }}
            onClick={() => {
              usedViaPointer('add-layer');
              pendingLayerEdit.current = true;
              addLayer(`Layer ${layers.length + 1}`, LAYER_PALETTE[layers.length % LAYER_PALETTE.length]);
            }}
          >+</button>
        )}
        {bottomPanelTab === 'flows' && !isViewOnly && (
          <button
            title="Create flow from selection"
            className="px-3 text-base leading-none font-bold transition-colors hover:opacity-100 opacity-60"
            style={{ color: palette.panelSubText }}
            onClick={() => {
              const nodeIds = selectedItems.filter(id => nodes.some(n => n.id === id));
              const explicitEdgeIds = selectedItems.filter(id => edges.some(e => e.id === id));
              if (nodeIds.length === 0 && explicitEdgeIds.length === 0) return;
              const nodeIdSet = new Set(nodeIds);
              const internalEdgeIds = edges
                .filter(e => nodeIdSet.has(e.sourceNodeId) && nodeIdSet.has(e.targetNodeId))
                .map(e => e.id);
              const edgeIds = [...new Set([...explicitEdgeIds, ...internalEdgeIds])];
              addFlow(`Flow ${flows.length + 1}`, nodeIds, edgeIds);
            }}
          >+</button>
        )}
      </div>

      {/* Layers tab */}
      {bottomPanelTab === 'layers' && (
        <div className="flex-1 overflow-y-auto">
          {layers.map(layer => {
            const isActive = layer.id === activeLayerId;
            return (
              <div
                key={layer.id}
                className="group flex items-center gap-2 px-3 py-2 transition-colors cursor-pointer"
                style={{
                  background: isActive ? `${layer.color}15` : 'transparent',
                  borderLeft: isActive ? `3px solid ${layer.color}` : '3px solid transparent',
                }}
                onClick={() => setActiveLayer(layer.id)}
              >
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 ring-1 ring-white/20" style={{ backgroundColor: layer.color }} />
                {!isViewOnly && editingLayerId === layer.id ? (
                  <input
                    autoFocus
                    className="flex-1 text-xs bg-transparent border-b focus:outline-none min-w-0"
                    style={{ color: palette.panelText, borderColor: layer.color }}
                    defaultValue={layer.name}
                    onClick={e => e.stopPropagation()}
                    onKeyDown={e => {
                      if (e.key === 'Enter') e.currentTarget.blur();
                      if (e.key === 'Escape') setEditingLayerId(null);
                    }}
                    onBlur={e => {
                      const val = e.target.value.trim();
                      if (val) updateLayer(layer.id, { name: val });
                      setEditingLayerId(null);
                    }}
                  />
                ) : (
                  <span
                    className="flex-1 text-xs truncate"
                    style={{ color: isActive ? palette.panelText : palette.panelSubText, fontWeight: isActive ? 600 : 400 }}
                    onDoubleClick={e => { if (!isViewOnly) { e.stopPropagation(); setEditingLayerId(layer.id); } }}
                  >{layer.name}</span>
                )}
                {!isViewOnly && (
                  <button
                    title={layer.visible ? 'Hide' : 'Show'}
                    onClick={e => { e.stopPropagation(); usedViaPointer('hide-layer'); updateLayer(layer.id, { visible: !layer.visible }); }}
                    className="text-xs w-5 text-center transition-colors"
                    style={{ color: layer.visible ? palette.panelSubText : palette.panelBorder }}
                  >{layer.visible ? '👁' : '—'}</button>
                )}
                {!isViewOnly && (
                  <button
                    title={layer.dimmed ? 'Un-dim' : 'Dim'}
                    onClick={e => { e.stopPropagation(); usedViaPointer('dim-layer'); updateLayer(layer.id, { dimmed: !layer.dimmed }); }}
                    className="text-xs w-5 text-center transition-colors"
                    style={{ color: layer.dimmed ? '#818cf8' : palette.panelBorder }}
                  >◎</button>
                )}
                {!isViewOnly && layer.id !== 'default' && (
                  <button
                    title="Delete layer"
                    onClick={e => {
                      e.stopPropagation();
                      if (window.confirm(`Delete "${layer.name}"? Nodes will move to Default.`)) removeLayer(layer.id);
                    }}
                    className="text-xs w-5 text-center transition-colors opacity-0 group-hover:opacity-100"
                    style={{ color: '#ef4444' }}
                  >✕</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Flows tab */}
      {bottomPanelTab === 'flows' && (
        <div className="flex-1 overflow-y-auto flex flex-col">

          <div className="flex-1 overflow-y-auto">
            {flows.length === 0 && (
              <div className="px-4 py-3 text-[11px] italic" style={{ color: palette.panelBorder }}>
                {isViewOnly ? 'No flows in this diagram.' : 'Select nodes/edges then press + to create a flow.'}
              </div>
            )}
            {flows.map(flow => {
              const isActive = activeFlowId === flow.id;
              return (
                <div
                  key={flow.id}
                  className="group flex items-center gap-1.5 px-3 py-2 transition-colors cursor-pointer"
                  style={{
                    background: isActive ? '#818cf818' : 'transparent',
                    borderLeft: isActive ? '3px solid #818cf8' : '3px solid transparent',
                  }}
                  onClick={() => setActiveFlowId(isActive ? null : flow.id)}
                >
                  {/* Active indicator dot */}
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0 transition-colors"
                    style={{ background: isActive ? '#818cf8' : palette.panelBorder }}
                  />
                  {!isViewOnly && editingFlowId === flow.id ? (
                    <input
                      autoFocus
                      className="flex-1 text-xs bg-transparent border-b focus:outline-none min-w-0"
                      style={{ color: palette.panelText, borderColor: '#818cf8' }}
                      defaultValue={flow.name}
                      onClick={e => e.stopPropagation()}
                      onKeyDown={e => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                        if (e.key === 'Escape') setEditingFlowId(null);
                      }}
                      onBlur={e => {
                        const val = e.target.value.trim();
                        if (val) renameFlow(flow.id, val);
                        setEditingFlowId(null);
                      }}
                    />
                  ) : (
                    <span
                      className="flex-1 text-xs truncate"
                      style={{ color: isActive ? palette.panelText : palette.panelSubText, fontWeight: isActive ? 600 : 400 }}
                      onDoubleClick={e => { if (!isViewOnly) { e.stopPropagation(); setEditingFlowId(flow.id); } }}
                    >
                      {flow.name}
                    </span>
                  )}
                  {/* Update flow from selection */}
                  {!isViewOnly && (
                    <button
                      title="Update with current selection"
                      className="text-[10px] opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-opacity flex-shrink-0"
                      style={{ color: palette.panelSubText }}
                      onClick={e => {
                        e.stopPropagation();
                        const nodeIds = selectedItems.filter(id => nodes.some(n => n.id === id));
                        const edgeIds = selectedItems.filter(id => edges.some(ed => ed.id === id));
                        updateFlow(flow.id, nodeIds, edgeIds);
                      }}
                    >↺</button>
                  )}
                  {/* Delete */}
                  {!isViewOnly && (
                    <button
                      title="Delete flow"
                      className="text-[10px] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                      style={{ color: '#ef4444' }}
                      onClick={e => {
                        e.stopPropagation();
                        removeFlow(flow.id);
                        if (activeFlowId === flow.id) setActiveFlowId(null);
                      }}
                    >✕</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
